import React from "react";
import { Typography, Chip } from "@mui/material";
import { GridActionsCellItem, GridRowModes } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { getOrderStateBadgeStyles } from "../utils/orderStateColors";
import Utils from "../../../utils/Utils";
import OrderStates from "../../../utils/OrderStates";
import EtiquetasPedidoN2 from "../components/ui/EtiquetasPedidoN2";
import { ESTADO_PROCESO } from "../../../config/constants";

/**
 * Columnas del DataGrid de grupos de Nave 2
 *
 * `ordersModels` decide si la fila esta en edicion: de ahi depende que se pinten
 * las acciones de guardar/cancelar o las de editar.
 */
export const generateGrupoColumns = ({ getNombreAlmacen, ordersModels, onEditar, onGuardar, onCancelar, onForzar, productosPorPedido = {} }) => [
        {
            field: 'num',
            headerName: 'Número',
            width: 100,
            // Enlace al punteo del pedido suelto, el mismo que en el listado de Nave 2.
            // stopPropagation: el click no debe marcar además la casilla de selección
            renderCell: (params) => (
                <Typography
                    component="a"
                    href={'#/pedidosn2/order/' + params.value + '?tipo=' + params.row.type + '&ruta=' + params.row.route}
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ fontWeight: 600, color: '#cc071e', fontSize: 13 }}
                >
                    {params.value}
                </Typography>
            )
        },
        {
            field: 'type',
            headerName: 'Tipo',
            width: 90,
            renderCell: (params) => Utils.toCapitalize(params.value)
        },
        {
            field: 'date',
            headerName: 'Fecha',
            width: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => params.value ? Utils.formatDateWithoutHour(params.value) : '-'
        },
        {
            field: 'customer',
            headerName: 'Cliente',
            flex: 2,
            minWidth: 160,
            renderCell: (params) => Utils.toCapitalize(params.value),
            filterMode: 'client'
        },
        {
            field: 'route',
            headerName: 'Ruta',
            flex: 1,
            minWidth: 110,
            renderCell: (params) => Utils.toCapitalize(params.value)
        },
        {
            field: 'transport',
            headerName: 'Transporte',
            flex: 1.2,
            minWidth: 130,
            renderCell: (params) => params.value ? Utils.toCapitalize(params.value) : '-'
        },
        {
            field: 'preparerN1',
            headerName: 'Prep. nave 1',
            flex: 1,
            minWidth: 120,
            renderCell: (params) => params.value || ESTADO_PROCESO.SIN_PREPARADOR
        },
        {
            field: 'stateN1',
            headerName: 'Estado nave 1',
            width: 130,
            align: 'center',
            headerAlign: 'center',
            filterable: false,
            renderCell: (params) => (
                <Chip
                    label={OrderStates.getState(params.value)}
                    size="small"
                    sx={getOrderStateBadgeStyles(params.value, { fontSize: 12 })}
                />
            )
        },
        {
            field: 'state',
            headerName: 'Estado',
            width: 120,
            align: 'center',
            headerAlign: 'center',
            filterable: false,
            renderCell: (params) => (
                <Chip
                    label={OrderStates.getState(params.value)}
                    size="small"
                    sx={getOrderStateBadgeStyles(params.value, { fontSize: 12 })}
                />
            )
        },
        {
            field: 'warehouse',
            headerName: 'Nave',
            width: 100,
            filterable: false,
            renderCell: (params) => getNombreAlmacen ? getNombreAlmacen(params.value) : params.value
        },
        {
            field: 'exclude',
            headerName: 'No preparar',
            type: 'boolean',
            width: 105,
            align: 'center',
            headerAlign: 'center',
            editable: true
        },
        {
            field: 'actions',
            type: 'actions',
            headerName: '',
            // Etiquetas (1-2 botones) + editar + forzar
            width: 170,
            cellClassName: 'actions',
            getActions: ({ id, row }) => {
                const isInEditMode = ordersModels[id]?.mode === GridRowModes.Edit;

                if (isInEditMode) {
                    return [
                        <GridActionsCellItem
                            icon={<SaveIcon />}
                            label="Save"
                            sx={{
                                color: 'primary.main'
                            }}
                            onClick={() => onGuardar(id)}
                        />,
                        <GridActionsCellItem
                            icon={<CancelIcon />}
                            label="Cancel"
                            className="textPrimary"
                            onClick={() => onCancelar(id)}
                            color="inherit"
                        />
                    ];
                }

                return [
                    <GridActionsCellItem
                        icon={<EditIcon />}
                        label="Edit"
                        className="textPrimary"
                        onClick={() => onEditar(id)}
                        color="inherit"
                    />,
                    // Sacar de la lista un pedido colgado (opcional: solo si la página lo pide)
                    ...(onForzar ? [
                        <GridActionsCellItem
                            icon={<DoneAllIcon />}
                            label="Forzar preparado"
                            title="Forzar como preparado y quitar de la lista"
                            onClick={() => onForzar(row)}
                            color="inherit"
                        />
                    ] : []),
                    // DECISION: las etiquetas van junto a editar y forzar, no en columna
                    // aparte. La celda de acciones clona cada elemento y le añade onClick,
                    // tabIndex y touchRippleRef; EtiquetasPedidoN2 los ignora (no los pasa
                    // al DOM) y para la propagación del clic él mismo, así no marca la fila
                    <EtiquetasPedidoN2 pedido={row} productos={productosPorPedido[row.num]} />
                ];
            }
        }
];
